import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoader } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { DataTable } from "@/components/common/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Briefcase, Plus, FileText, Gift, ExternalLink, Copy, Download } from "lucide-react";
import { viewAttachment } from "@/lib/files";
import { toast } from "sonner";

export async function viewResume(aid, filename) {
  try { await viewAttachment(`/attachments/${aid}/file`, filename); }
  catch (e) { toast.error(apiError(e)); }
}

const JOB_STATUS = { draft: "Draft", open: "Open", paused: "Paused", closed: "Closed", archived: "Archived" };
const JOB_BADGE = { draft: "bg-slate-100 text-slate-600", open: "bg-emerald-100 text-emerald-700",
  paused: "bg-amber-100 text-amber-700", closed: "bg-rose-100 text-rose-600", archived: "bg-slate-100 text-slate-400" };
const APP_STATUS = { new: "New", reviewed: "Reviewed", advanced: "Advanced", rejected: "Rejected", withdrawn: "Withdrawn" };
const TABS = [{ k: "jobs", l: "Jobs", i: Briefcase }, { k: "applications", l: "Applications", i: FileText }, { k: "referrals", l: "Referrals", i: Gift }];

export default function Jobs() {
  const [tab, setTab] = useState("jobs");
  const [addJob, setAddJob] = useState(false);
  const [addRef, setAddRef] = useState(false);
  const [key, setKey] = useState(0);
  const refresh = () => setKey((k) => k + 1);
  return (
    <div>
      <PageHeader title="Jobs & Intake" description="Post jobs, receive applications and referral submissions."
        actions={<div className="flex flex-wrap gap-2">
          {tab === "referrals" && <Button variant="outline" onClick={() => setAddRef(true)} data-testid="add-referral-button"><Gift className="w-4 h-4 mr-1.5" />New Referral</Button>}
          <Button className="bg-brand hover:bg-brand-strong" onClick={() => setAddJob(true)} data-testid="add-job-button"><Plus className="w-4 h-4 mr-1.5" />New Job</Button>
        </div>} />
      <div className="flex items-center gap-1 border-b border-slate-200 mb-5 overflow-x-auto tr-scroll [&>*]:shrink-0" data-testid="jobs-tabs">
        {TABS.map((t) => (
          <button key={t.k} data-testid={`jobs-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === t.k ? "border-brand text-brand" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <t.i className="w-4 h-4" />{t.l}</button>
        ))}
      </div>
      {tab === "jobs" && <JobsList key={`j${key}`} />}
      {tab === "applications" && <ApplicationsList key={`a${key}`} />}
      {tab === "referrals" && <ReferralsList key={`r${key}`} />}
      {addJob && <JobDialog onClose={() => setAddJob(false)} onSaved={() => { setAddJob(false); refresh(); }} />}
      {addRef && <ReferralDialog onClose={() => setAddRef(false)} onSaved={() => { setAddRef(false); refresh(); }} />}
    </div>
  );
}

function JobsList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/jobs").then((r) => setRows(r.data.items)).catch((e) => { toast.error(apiError(e)); setRows([]); }); }, []);
  if (!rows) return <PageLoader />;
  if (!rows.length) return <EmptyState icon={Briefcase} title="No jobs yet" description="Create a job to start receiving applications." />;
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="jobs-grid">
      {rows.map((j) => (
        <button key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`job-card-${j.id}`}
          className="text-left rounded-xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md hover:border-brand/40 transition-all">
          <div className="flex items-center justify-between mb-2"><Briefcase className="w-5 h-5 text-brand" />
            <span className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${JOB_BADGE[j.status]}`}>{JOB_STATUS[j.status]}</span></div>
          <h3 className="font-semibold text-slate-900 truncate">{j.title}</h3>
          <p className="text-xs text-slate-400">{j.human_id}{j.location ? ` · ${j.location}` : ""}</p>
          <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
            <span>{j.organization_name || "—"}</span>
            <span className="ml-auto flex items-center gap-1"><FileText className="w-3 h-3" />{j.application_count} apps</span>
          </div>
        </button>
      ))}
    </div>
  );
}

function ApplicationsList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/applications").then((r) => setRows(r.data.items)).catch((e) => { toast.error(apiError(e)); setRows([]); }), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <PageLoader />;
  const setStatus = async (a, v) => { try { await api.patch(`/applications/${a.id}`, { status: v }); load(); } catch (e) { toast.error(apiError(e)); } };
  const cols = [
    { key: "applicant", header: "Applicant", render: (r) => <button onClick={(e) => { e.stopPropagation(); navigate(`/people/detail/${r.person_id}`); }} className="font-medium text-slate-800 hover:text-brand">{r.applicant_name}</button> },
    { key: "job", header: "Job", render: (r) => r.job_title || "—" },
    { key: "ctype", header: "Candidate Type", render: (r) => r.candidate_type || "—" },
    { key: "source", header: "Source", render: (r) => r.source || "—" },
    { key: "resume", header: "Résumé", render: (r) => r.attachment_id ? (
      <button onClick={(e) => { e.stopPropagation(); viewResume(r.attachment_id); }} data-testid={`app-resume-${r.id}`}
        className="text-brand hover:underline text-sm flex items-center gap-1"><Download className="w-3.5 h-3.5" />View</button>
    ) : <span className="text-slate-300">—</span> },
    { key: "submitted", header: "Submitted", render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : "—" },
    { key: "recruiter", header: "Recruiter", render: (r) => r.recruiter_name || "—" },
    { key: "stage", header: "Stage", render: (r) => r.stage_name || "—" },
    { key: "status", header: "Status", render: (r) => (
      <Select value={r.status} onValueChange={(v) => setStatus(r, v)}>
        <SelectTrigger className="h-8 w-32 text-xs" data-testid={`app-status-${r.id}`} onClick={(e) => e.stopPropagation()}><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white">{Object.entries(APP_STATUS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
      </Select>) },
  ];
  return <DataTable columns={cols} rows={rows} testId="applications-table"
    empty={<EmptyState icon={FileText} title="No applications" description="Applications from public jobs will appear here." />} />;
}

function ReferralsList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/referral-submissions").then((r) => setRows(r.data.items)).catch((e) => { toast.error(apiError(e)); setRows([]); }); }, []);
  if (!rows) return <PageLoader />;
  const cols = [
    { key: "candidate", header: "Candidate", render: (r) => <button onClick={() => navigate(`/people/detail/${r.person_id}`)} className="font-medium text-slate-800 hover:text-brand">{r.candidate_name}</button> },
    { key: "partner", header: "Referred By", render: (r) => r.partner_name || "—" },
    { key: "ctype", header: "Program", render: (r) => r.candidate_type || "—" },
    { key: "submitted", header: "Submitted", render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : "—" },
    { key: "ack", header: "Acknowledgment", render: (r) => r.acknowledgment_requested ? <span className="text-xs rounded-full bg-sky-100 text-sky-700 px-2 py-0.5">{r.acknowledgment_status}</span> : <span className="text-slate-300">—</span> },
    { key: "status", header: "Status", render: (r) => <span className="text-xs rounded-full bg-slate-100 text-slate-600 px-2 py-0.5">{r.status}{r.duplicate_flagged ? " · dup" : ""}</span> },
  ];
  return <DataTable columns={cols} rows={rows} testId="referrals-table"
    empty={<EmptyState icon={Gift} title="No referral submissions" description="Referral partner submissions will appear here." />} />;
}

export function JobDialog({ job, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", organization_id: "", candidate_type: "", assigned_recruiter_id: "",
    location: "", employment_type: "", description: "", status: "open", is_public: true, ...(job || {}) });
  const [orgs, setOrgs] = useState([]); const [cts, setCts] = useState([]); const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    Promise.all([api.get("/organizations"), api.get("/candidate-types"), api.get("/tasks/assignable-users")])
      .then(([o, c, u]) => { setOrgs(o.data.organizations); setCts(c.data.items); setUsers(u.data.items); }).catch(() => {});
  }, []);
  const N = "__none__";
  const submit = async () => {
    if (!form.title.trim()) return toast.error("Job title is required");
    setSaving(true);
    try {
      const payload = { ...form, organization_id: form.organization_id || null, candidate_type: form.candidate_type || null,
        assigned_recruiter_id: form.assigned_recruiter_id || null };
      if (job) await api.patch(`/jobs/${job.id}`, payload); else await api.post("/jobs", payload);
      toast.success(job ? "Job updated" : "Job created"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="job-dialog">
        <DialogHeader><DialogTitle>{job ? "Edit Job" : "New Job"}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto tr-scroll pr-1">
          <div><Label className="text-xs font-semibold">Job Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="job-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">Organization</Label>
              <Select value={form.organization_id || N} onValueChange={(v) => setForm({ ...form, organization_id: v === N ? "" : v })}>
                <SelectTrigger data-testid="job-org"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-white max-h-56"><SelectItem value={N}>None</SelectItem>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs font-semibold">Candidate Type</Label>
              <Select value={form.candidate_type || N} onValueChange={(v) => setForm({ ...form, candidate_type: v === N ? "" : v })}>
                <SelectTrigger data-testid="job-ctype"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-white max-h-56"><SelectItem value={N}>None</SelectItem>{cts.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">Assigned Recruiter</Label>
              <Select value={form.assigned_recruiter_id || N} onValueChange={(v) => setForm({ ...form, assigned_recruiter_id: v === N ? "" : v })}>
                <SelectTrigger data-testid="job-recruiter"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-white max-h-56"><SelectItem value={N}>None</SelectItem>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs font-semibold">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="job-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{Object.entries(JOB_STATUS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="job-location" /></div>
            <div><Label className="text-xs font-semibold">Employment Type</Label><Input value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })} placeholder="Full-time" /></div>
          </div>
          <div><Label className="text-xs font-semibold">Description</Label><Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="job-description" /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} data-testid="job-public" /> Publicly listed on Careers page</label>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-brand hover:bg-brand-strong" data-testid="job-submit">{job ? "Save" : "Create Job"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReferralDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ referral_partner_person_id: "", candidate_type: "", organization_id: "",
    assigned_recruiter_id: "", notes: "", send_acknowledgment: false,
    candidate: { first_name: "", last_name: "", email: "", mobile: "", city: "", state: "" } });
  const [people, setPeople] = useState([]); const [orgs, setOrgs] = useState([]); const [cts, setCts] = useState([]); const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    Promise.all([api.get("/persons", { params: { page_size: 300 } }), api.get("/organizations"), api.get("/candidate-types"), api.get("/tasks/assignable-users")])
      .then(([p, o, c, u]) => { setPeople(p.data.persons); setOrgs(o.data.organizations); setCts(c.data.items); setUsers(u.data.items); }).catch(() => {});
  }, []);
  const N = "__none__";
  const c = form.candidate;
  const setC = (k, v) => setForm((f) => ({ ...f, candidate: { ...f.candidate, [k]: v } }));
  const submit = async () => {
    if (!form.referral_partner_person_id) return toast.error("Select the referral partner");
    if (!c.first_name || !c.last_name || !c.email || !c.mobile) return toast.error("Candidate name, email and mobile are required");
    setSaving(true);
    try {
      await api.post("/referral-submissions", { ...form, organization_id: form.organization_id || null,
        candidate_type: form.candidate_type || null, assigned_recruiter_id: form.assigned_recruiter_id || null });
      toast.success("Referral received successfully"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="referral-dialog">
        <DialogHeader><DialogTitle>Submit a Referral</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[62vh] overflow-y-auto tr-scroll pr-1">
          <div><Label className="text-xs font-semibold">Referral Partner *</Label>
            <Select value={form.referral_partner_person_id || N} onValueChange={(v) => setForm({ ...form, referral_partner_person_id: v === N ? "" : v })}>
              <SelectTrigger data-testid="ref-partner"><SelectValue placeholder="Select partner" /></SelectTrigger>
              <SelectContent className="bg-white max-h-56"><SelectItem value={N}>Select</SelectItem>{people.map((p) => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.human_id})</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">Candidate First *</Label><Input value={c.first_name} onChange={(e) => setC("first_name", e.target.value)} data-testid="ref-first" /></div>
            <div><Label className="text-xs font-semibold">Candidate Last *</Label><Input value={c.last_name} onChange={(e) => setC("last_name", e.target.value)} data-testid="ref-last" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">Email *</Label><Input value={c.email} onChange={(e) => setC("email", e.target.value)} data-testid="ref-email" /></div>
            <div><Label className="text-xs font-semibold">Mobile *</Label><Input value={c.mobile} onChange={(e) => setC("mobile", e.target.value)} data-testid="ref-mobile" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">City</Label><Input value={c.city} onChange={(e) => setC("city", e.target.value)} /></div>
            <div><Label className="text-xs font-semibold">State</Label><Input value={c.state} onChange={(e) => setC("state", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">Program / Candidate Type</Label>
              <Select value={form.candidate_type || N} onValueChange={(v) => setForm({ ...form, candidate_type: v === N ? "" : v })}>
                <SelectTrigger data-testid="ref-ctype"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-white max-h-56"><SelectItem value={N}>None</SelectItem>{cts.map((x) => <SelectItem key={x.key} value={x.key}>{x.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs font-semibold">Assign Recruiter</Label>
              <Select value={form.assigned_recruiter_id || N} onValueChange={(v) => setForm({ ...form, assigned_recruiter_id: v === N ? "" : v })}>
                <SelectTrigger data-testid="ref-recruiter"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="bg-white max-h-56"><SelectItem value={N}>None</SelectItem>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div><Label className="text-xs font-semibold">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="ref-notes" /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.send_acknowledgment} onChange={(e) => setForm({ ...form, send_acknowledgment: e.target.checked })} data-testid="ref-ack" /> Send Candidate Acknowledgment</label>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-brand hover:bg-brand-strong" data-testid="ref-submit">Submit Referral</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
